// Click-IDs we read straight from the landing URL's query string.
const URL_PARAM_KEYS = [
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'li_fat_id',
  'msclkid',
  'rdt_cid',
] as const;

// Meta cookie-derived identifiers. The app's Meta pixel writes `_fbc`/`_fbp`;
// the landing site also forwards them as `fbc`/`fbp` URL params.
const COOKIE_PARAMS = [
  { key: 'fbc', cookie: '_fbc' },
  { key: 'fbp', cookie: '_fbp' },
] as const;

const ATTRIBUTION_KEYS = [
  ...URL_PARAM_KEYS,
  'fbc',
  'fbp',
  'landing_page',
  'referrer',
] as const;

type AttributionParams = Partial<Record<(typeof ATTRIBUTION_KEYS)[number], string>>;

const STORAGE_KEY = 'signup_attribution';

// Mirrors KNOWN_DOMAINS in src/utils/cookie_utils.py. Cookies are scoped to the
// matching apex so the cross-origin backend (e.g. prod-api.percuity.ai) sees
// them on the federated OAuth callback — sessionStorage doesn't travel there.
const APEX_DOMAINS = ['percuity.ai', 'leoads.ai', 'fibonaccipartner.com'];

/** Reads a single cookie value by name, or null when absent. */
function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function getAttributionCookieDomain(): string | null {
  const host = window.location.hostname;
  for (const apex of APEX_DOMAINS) {
    if (host === apex || host.endsWith('.' + apex)) return '.' + apex;
  }
  return null; // localhost / dev hosts → host-only cookie
}

/**
 * Reads ad click-IDs (URL params + Meta pixel cookies) from the current page
 * and persists them in sessionStorage so they survive navigation within the
 * app. First-touch: an existing capture is never overwritten.
 * Should be called once on app startup (mirrors captureUtmParams).
 */
export function captureAttributionParams(): void {
  if (sessionStorage.getItem(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const attribution: AttributionParams = {};

  for (const key of URL_PARAM_KEYS) {
    const val = params.get(key);
    if (val) attribution[key] = val;
  }

  for (const { key, cookie } of COOKIE_PARAMS) {
    const val = params.get(key) || readCookie(cookie);
    if (val) attribution[key] = val;
  }

  if (window.location.pathname) attribution.landing_page = window.location.pathname;
  if (document.referrer) attribution.referrer = document.referrer;

  if (Object.keys(attribution).length > 0) {
    const serialized = JSON.stringify(attribution);
    sessionStorage.setItem(STORAGE_KEY, serialized);

    // Mirror to a parent-domain cookie so the federated OAuth callback (on the
    // separate -api host) can fold attribution into the server-side CAPI event.
    const domainAttr = getAttributionCookieDomain();
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie =
      `${STORAGE_KEY}=${encodeURIComponent(serialized)}; expires=${expires}; path=/` +
      (domainAttr ? `; domain=${domainAttr}` : '') +
      '; SameSite=Lax';
  }
}

export function getStoredAttributionParams(): AttributionParams {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}
